import { Select, Spin } from 'antd'
import { SelectTokenizeType } from 'Modules'
import { useCallback, useEffect, useRef, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { logError, logInfo } from 'utils/logger'

const SelectTokenize = ({
  value,
  onChange,
  onTokenSelected,
  disabled,
  stakingTokenize,
  tokenize,
}: any) => {
  const [options, setTokenOptions] = useState<SelectTokenizeType[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [_, setPage] = useState(DEFAULT_PAGE_INDEX)
  const isLoadingRef = useRef(false)

  const fetchTokens = useCallback(
    async (pageNum: number) => {
      if (isLoadingRef.current) {
        logInfo('Already loading, skip')
        return
      }

      isLoadingRef.current = true
      setLoading(true)
      logInfo('Fetching page:', pageNum)

      try {
        const results = await AppAdmin.getSelectPTO({
          page: pageNum,
          limit: DEFAULT_PAGE_SIZE,
          stakingTokenize,
          tokenize: tokenize || null,
        })

        if (results.status === 200) {
          const { results: data, totalPages } = results.data.data || {}

          logInfo('Received:', data?.length, 'items. Page', pageNum, 'of', totalPages)

          const nftCollections = data.map((item: SelectTokenizeType) => ({
            ...item,
            value: item._id,
            label: item.fullName,
          }))

          setTokenOptions((prev) =>
            pageNum === DEFAULT_PAGE_INDEX ? nftCollections : [...prev, ...nftCollections]
          )

          setHasMore(pageNum < totalPages)
        }
      } catch (error) {
        logError('Error:', error)
        setHasMore(false)
      } finally {
        setLoading(false)
        setTimeout(() => {
          isLoadingRef.current = false
        }, 300)
      }
    },
    [stakingTokenize, tokenize]
  )

  useEffect(() => {
    fetchTokens(DEFAULT_PAGE_INDEX)
  }, [fetchTokens])

  const handleChange = async (val: string) => {
    const token = options.find((t) => t.value === val)
    onChange(val)
    if (onTokenSelected) {
      await onTokenSelected(token)
    }
  }

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    const { scrollTop, scrollHeight, clientHeight } = target
    const scrollPercentage = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100)

    if (scrollPercentage > 80) {
      logInfo('Scroll:', scrollPercentage + '%')
    }

    if (scrollPercentage > 90 && hasMore && !isLoadingRef.current) {
      logInfo('Trigger load more!')
      setPage((prev) => {
        const next = prev + 1
        fetchTokens(next)
        return next
      })
    }
  }

  return (
    <Select
      showSearch
      value={value}
      placeholder="Select a tokenize"
      onChange={handleChange}
      disabled={disabled}
      optionFilterProp="label"
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
      onPopupScroll={handlePopupScroll}
      virtual={true}
      listHeight={300}
      notFoundContent={loading && options.length === 0 ? <Spin size="small" /> : 'No data'}
      dropdownStyle={{ padding: 8 }}
      dropdownRender={(menu) => (
        <>
          {menu}
          {loading && options.length > 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: 12,
                borderTop: '1px solid #f0f0f0',
              }}
            >
              <Spin size="small" />
              <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>Loading more...</div>
            </div>
          )}
        </>
      )}
      options={options}
    />
  )
}

export default SelectTokenize
